// Config panel for the "regex_extract" action block
// Applies regex patterns to variables or trigger data to extract sub-values
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormHelperText,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const RegexExtractConfig = ({ config, onChange, onSummaryChange }) => {
  const [testInput, setTestInput] = useState('');
  const [testResults, setTestResults] = useState(null);

  const extractions = config.extractions || [{ inputVariable: '', pattern: '', outputVariable: '', groupIndex: 0 }];

  // Count capture groups in a regex pattern by counting unescaped '('
  const countGroups = (pattern) => {
    if (!pattern) return 0;
    try {
      // Use the regex itself to count groups via exec on empty string trick
      const re = new RegExp(pattern);
      const match = ''.match(re);
      // Fallback: count unescaped opening parens that are capture groups (not (?:...) etc.)
      let count = 0;
      for (let i = 0; i < pattern.length; i++) {
        if (pattern[i] === '\\') { i++; continue; }
        if (pattern[i] === '(' && pattern[i + 1] !== '?') count++;
      }
      return count;
    } catch { return 0; }
  };

  const updateExtractions = (newExtractions) => {
    const updated = { ...config, extractions: newExtractions };
    onChange(updated);
    const named = newExtractions.filter(e => e.outputVariable).map(e => e.outputVariable);
    onSummaryChange(named.length > 0 ? `regex → ${named.join(', ')}` : 'No extractions');
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = extractions.map((e, i) =>
      i === index ? { ...e, [field]: value } : e
    );
    updateExtractions(newEntries);
  };

  const addExtraction = () => {
    updateExtractions([...extractions, { inputVariable: '', pattern: '', outputVariable: '', groupIndex: 0 }]);
  };

  const removeExtraction = (index) => {
    if (extractions.length <= 1) return;
    updateExtractions(extractions.filter((_, i) => i !== index));
  };

  // Live test a regex pattern against input
  const handleTest = () => {
    setTestResults(extractions.map(ext => {
      if (!ext.pattern || !testInput) return { match: null, error: null };
      try {
        const regex = new RegExp(ext.pattern);
        const match = testInput.match(regex);
        if (!match) return { match: null, error: null };
        const idx = ext.groupIndex || 0;
        return { match: match[idx] ?? null, groups: match.slice(0), error: null };
      } catch (e) {
        return { match: null, error: e.message };
      }
    }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Applies regex patterns to variable values and stores the matched
        groups as new variables for downstream blocks.
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption" component="div">
          <strong>How capture groups work</strong><br />
          Parentheses <code style={{ fontSize: '0.65rem' }}>(…)</code> in your regex pattern create <strong>capture groups</strong>.
          Each pair of parentheses captures the text it matches, numbered left to right.
        </Typography>
        <Box sx={{ mt: 1, mb: 0.5, p: 1, bgcolor: 'grey.50', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>
          <Box component="span" sx={{ color: 'text.secondary' }}>Input: </Box>
          sip:20024@10.20.30.205:5060<br />
          <Box component="span" sx={{ color: 'text.secondary' }}>Pattern: </Box>
          sip:<Box component="span" sx={{ color: '#e65100', fontWeight: 700 }}>(\d+)</Box>@<Box component="span" sx={{ color: '#1565c0', fontWeight: 700 }}>([\d.]+)</Box>:<Box component="span" sx={{ color: '#2e7d32', fontWeight: 700 }}>(\d+)</Box>
        </Box>
        <Table size="small" sx={{ mt: 0.5, '& td': { py: 0.25, px: 0.5, fontSize: '0.65rem', border: 0 } }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 80 }}>Group 0</TableCell>
              <TableCell><code style={{ fontSize: '0.65rem' }}>sip:20024@10.20.30.205:5060</code></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Full match (everything the pattern matched)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#e65100', width: 80 }}>Group 1</TableCell>
              <TableCell><code style={{ fontSize: '0.65rem' }}>20024</code></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>1st parentheses → extension number</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#1565c0', width: 80 }}>Group 2</TableCell>
              <TableCell><code style={{ fontSize: '0.65rem' }}>10.20.30.205</code></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>2nd parentheses → IP address</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#2e7d32', width: 80 }}>Group 3</TableCell>
              <TableCell><code style={{ fontSize: '0.65rem' }}>5060</code></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>3rd parentheses → port number</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          To extract specific parts, add multiple extractions each picking a different group number.
        </Typography>
      </Alert>

      {extractions.map((entry, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={600}>Extraction {index + 1}</Typography>
            {extractions.length > 1 && (
              <IconButton size="small" onClick={() => removeExtraction(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <TextField
            size="small"
            label="Input Variable"
            value={entry.inputVariable || ''}
            onChange={(e) => handleEntryChange(index, 'inputVariable', e.target.value)}
            required
            helperText={'Use {{varName}} syntax, e.g. "{{sip}}" or "{{query.format}}"'}
            placeholder="e.g. {{sip}}"
          />

          <TextField
            size="small"
            label="Regex Pattern"
            value={entry.pattern || ''}
            onChange={(e) => handleEntryChange(index, 'pattern', e.target.value)}
            required
            helperText="Wrap the parts you want to extract in parentheses (...). Each pair = one capture group."
            placeholder="e.g. sip:(\d+)@([\d.]+):(\d+)"
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />

          {(() => {
            const groupCount = countGroups(entry.pattern);
            return (
              <FormControl fullWidth size="small">
                <InputLabel>Capture Group</InputLabel>
                <Select
                  value={entry.groupIndex ?? 0}
                  onChange={(e) => handleEntryChange(index, 'groupIndex', e.target.value)}
                  label="Capture Group"
                >
                  <MenuItem value={0}>
                    Group 0 — Full match (entire matched text)
                  </MenuItem>
                  {[1, 2, 3, 4, 5].map(i => (
                    <MenuItem key={i} value={i} disabled={i > groupCount}>
                      Group {i} — {i <= groupCount
                        ? `${i === 1 ? '1st' : i === 2 ? '2nd' : i === 3 ? '3rd' : `${i}th`} set of parentheses (...)`
                        : 'No parentheses found for this group'}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {groupCount === 0 && entry.pattern
                    ? 'Your pattern has no capture groups — add parentheses (...) around the part you want to extract'
                    : groupCount > 0
                      ? `Your pattern has ${groupCount} capture group${groupCount > 1 ? 's' : ''} — each (...) in your pattern is a group`
                      : 'Select which part of the match to extract. Group 0 = full match, Group 1+ = specific (...) captures'}
                </FormHelperText>
              </FormControl>
            );
          })()}

          <TextField
            size="small"
            label="Output Variable Name"
            value={entry.outputVariable || ''}
            onChange={(e) => handleEntryChange(index, 'outputVariable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            required
            helperText={'Stored as {{variableName}} for downstream blocks'}
            placeholder="e.g. sipExtension"
          />

          {testResults && testResults[index] && (
            <Box>
              {testResults[index].error ? (
                <Chip label={`Error: ${testResults[index].error}`} size="small" color="error" />
              ) : testResults[index].groups ? (
                <Paper variant="outlined" sx={{ p: 1, bgcolor: 'success.50' }}>
                  <Typography variant="caption" fontWeight={600} color="success.main" sx={{ mb: 0.5, display: 'block' }}>
                    Match found — All groups:
                  </Typography>
                  {testResults[index].groups.map((g, gi) => (
                    <Box key={gi} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <Chip
                        label={`Group ${gi}`}
                        size="small"
                        variant={gi === (entry.groupIndex ?? 0) ? 'filled' : 'outlined'}
                        color={gi === (entry.groupIndex ?? 0) ? 'success' : 'default'}
                        sx={{ minWidth: 65, fontSize: '0.65rem' }}
                      />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {g ?? <em style={{ color: '#999' }}>empty</em>}
                      </Typography>
                      {gi === (entry.groupIndex ?? 0) && (
                        <Typography variant="caption" color="success.main" fontWeight={600} sx={{ fontSize: '0.6rem' }}>
                          ← selected
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Paper>
              ) : (
                <Chip label="No match — pattern did not match the test input" size="small" color="warning" />
              )}
            </Box>
          )}
        </Paper>
      ))}

      <Button size="small" startIcon={<AddIcon />} onClick={addExtraction} variant="outlined">
        Add Extraction
      </Button>

      {/* Live test area */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>Test Regex</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label="Test input value"
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            sx={{ flex: 1 }}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            placeholder="e.g. sip:20024@10.20.30.205:5060"
          />
          <Button size="small" variant="contained" onClick={handleTest}>
            Test
          </Button>
        </Box>
      </Paper>

      {extractions.some(e => e.outputVariable) && (
        <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
          <Typography variant="caption">
            <strong>Output variables:</strong><br />
            {extractions.filter(e => e.outputVariable).map(e => (
              <span key={e.outputVariable}>
                <code style={{ fontSize: '0.7rem' }}>{`{{${e.outputVariable}}}`}</code><br />
              </span>
            ))}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default RegexExtractConfig;
